/**
 * ============================================================
 *  3D Product Builder 主逻辑文件
 *  负责：Three.js 多模型渲染、选项生成、状态管理、价格计算、购物车
 * ============================================================
 *
 *  你通常不需要修改这个文件。
 *  所有可配置内容都在 builder-config.js 里。
 *
 *  核心功能：
 *  1. 多 GLB 文件分别加载（按 partType 分组）
 *  2. DRACOLoader 解码压缩模型
 *  3. 按 partType 改变颜色
 *  4. 按深度/高度切换模型可见性
 *  5. Shopify 变体映射 + Ajax 加购
 * ============================================================
 */

import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";

/* ============================================================
 *  全局变量
 * ============================================================ */

const config = window.ProductBuilderConfig;

// Three.js 对象
let scene = null;
let camera = null;
let renderer = null;
let controls = null;
let mainModelGroup = new THREE.Group();

// 用户选择状态
const state = {
  isModelLoaded: false,
  loadedCount: 0,
  totalModels: 0,
  // 颜色状态：{ main: "#2c3e50", crossmember: "#2c3e50", ... }
  colorState: {},
  // 已加载的模型引用：{ main: [model1, ...], crossmember: [model1, ...], ... }
  partMeshes: {},
  // 按钮选项状态：{ rack_height: "93", rack_depth: "41", ... }
  selectedOptions: {},
  // 配件数量：{ pull_up_bar: 0, protective_bar: 0, ... }
  accessories: {},
  // 当前深度和高度（控制模型可见性）
  activeDepth: "41",
  activeHeight: "93",
  isAddingToCart: false
};

/* ============================================================
 *  DOM 缓存
 * ============================================================ */

const DOM = {
  get messageContainer() { return document.getElementById("builder-message"); },
  get totalPrice() { return document.getElementById("builder-total"); },
  get cartResult() { return document.getElementById("builder-cart-result"); },
  get cartResultDetail() { return document.getElementById("builder-cart-detail"); },
  get cartResultTotal() { return document.getElementById("builder-cart-total"); },
  get loadingIndicator() { return document.getElementById("loading-indicator"); },
  get canvasWrap() { return document.getElementById("builder-canvas-wrap"); },
  get canvas() { return document.getElementById("builder-canvas"); },
  get zoomIn() { return document.getElementById("zoom-in"); },
  get zoomOut() { return document.getElementById("zoom-out"); },
  get addToCartBtn() { return document.getElementById("builder-add-to-cart"); },
  get checkoutBtn() { return document.getElementById("builder-checkout"); },
  get continueBtn() { return document.getElementById("builder-continue"); }
};

/* ============================================================
 *  初始化入口
 * ============================================================ */

function initBuilder() {
  if (!config) {
    console.error("ProductBuilderConfig 未找到，请确保 builder-config.js 已正确加载");
    return;
  }

  // 填充产品信息
  const titleEl = document.getElementById("builder-title");
  const nameEl = document.getElementById("builder-name");
  if (titleEl) titleEl.textContent = config.productTitle;
  if (nameEl) nameEl.textContent = config.productName;

  // 初始化状态
  initState();

  // 初始化 Three.js 场景
  initThree();

  // 加载所有模型
  loadAllModels();

  // 生成右侧选项面板
  renderOptions();

  // 绑定事件
  bindEvents();

  // 更新价格
  updatePrice();
}

/* ============================================================
 *  状态管理
 * ============================================================ */

function initState() {
  // 从 defaultColors 初始化颜色状态
  state.colorState = {};
  if (config.defaultColors) {
    for (const partType in config.defaultColors) {
      state.colorState[partType] = config.defaultColors[partType];
    }
  }

  // 初始化选项状态
  config.options.forEach(option => {
    if (option.type === "accessory") {
      // 配件用数量管理
      option.values.forEach(acc => {
        state.accessories[acc.value] = acc.defaultQty || 0;
      });
    } else if (option.values && option.values.length > 0) {
      // 其他选项默认选第一个
      state.selectedOptions[option.id] = option.values[0].value;
    }
  });

  // 设置当前深度和高度
  state.activeDepth = state.selectedOptions["rack_depth"] || "41";
  state.activeHeight = state.selectedOptions["rack_height"] || "93";
}

/* ============================================================
 *  Three.js 场景初始化
 * ============================================================ */

function initThree() {
  const canvas = DOM.canvas;
  const wrap = DOM.canvasWrap;
  if (!canvas || !wrap) return;

  const width = wrap.clientWidth;
  const height = wrap.clientHeight;

  // 场景
  scene = new THREE.Scene();
  scene.background = new THREE.Color("#f8f9fa");

  // 相机
  camera = new THREE.PerspectiveCamera(40, width / height, 0.1, 1000);
  const defaultViewName = config._defaultView || "front";
  const defaultView = config.cameraViews?.[defaultViewName] || { position: { x: 3.5, y: 2.8, z: 5.5 }, target: { x: 0, y: 1.5, z: 0 } };
  camera.position.set(defaultView.position.x, defaultView.position.y, defaultView.position.z);

  // 渲染器
  renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setSize(width, height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  // 控制器
  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.1;
  controls.target.set(defaultView.target.x, defaultView.target.y, defaultView.target.z);
  controls.minDistance = 1.5;
  controls.maxDistance = 12;
  controls.update();

  // 添加主模型组
  scene.add(mainModelGroup);

  // 灯光
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
  scene.add(ambientLight);

  const mainLight = new THREE.DirectionalLight(0xffffff, 1.2);
  mainLight.position.set(5, 10, 7);
  mainLight.castShadow = true;
  scene.add(mainLight);

  const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
  fillLight.position.set(-3, 2, 4);
  scene.add(fillLight);

  const backLight = new THREE.DirectionalLight(0xffffff, 0.3);
  backLight.position.set(0, 2, -5);
  scene.add(backLight);

  const rimLight = new THREE.DirectionalLight(0xffffff, 0.3);
  rimLight.position.set(-4, 3, -3);
  scene.add(rimLight);

  // 开始渲染循环
  animate();

  // 窗口大小变化
  window.addEventListener("resize", onResize);
}

function animate() {
  requestAnimationFrame(animate);
  if (controls) controls.update();
  if (renderer && scene && camera) renderer.render(scene, camera);
}

function onResize() {
  const wrap = DOM.canvasWrap;
  if (!wrap || !camera || !renderer) return;

  const width = wrap.clientWidth;
  const height = wrap.clientHeight;

  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setSize(width, height);
}

/* ============================================================
 *  多模型加载（按 partType 分组）
 * ============================================================ */

function loadAllModels() {
  if (!scene) {
    console.warn("场景未初始化，等待...");
    setTimeout(loadAllModels, 100);
    return;
  }

  const parts = config.model.parts;

  // 统计总模型数
  let total = 0;
  for (const key in parts) {
    total += parts[key].length;
  }
  state.totalModels = total;

  // 更新加载提示
  const loadingEl = DOM.loadingIndicator;
  if (loadingEl) {
    loadingEl.textContent = `Loading models... 0/${total}`;
    loadingEl.classList.remove("hidden");
  }

  // 创建 Loader
  const loader = new GLTFLoader();
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath(config.model.dracoPath);
  loader.setDRACOLoader(dracoLoader);

  // 遍历所有部件类型，逐个加载
  for (const partType in parts) {
    const models = parts[partType];
    if (!state.partMeshes[partType]) {
      state.partMeshes[partType] = [];
    }

    models.forEach(modelInfo => {
      loader.load(
        modelInfo.url,
        gltf => onModelLoaded(gltf, partType, modelInfo),
        xhr => onModelProgress(xhr, modelInfo.name),
        error => onModelError(error, modelInfo.name)
      );
    });
  }
}

function onModelLoaded(gltf, partType, modelInfo) {
  const model = gltf.scene;

  // flybird 需要旋转 180 度
  if (partType === "flybird") {
    model.rotation.y = Math.PI;
  }

  // 设置材质属性
  model.traverse(child => {
    if (child.isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
      if (child.material) {
        child.material.roughness = 0.4;
        child.material.metalness = 0.3;
      }
    }
  });

  // 应用缩放和位置
  if (config.model.scale) {
    model.scale.setScalar(config.model.scale);
  }
  if (config.model.position) {
    model.position.set(
      config.model.position.x,
      config.model.position.y,
      config.model.position.z
    );
  }

  // 存入 partMeshes
  if (!state.partMeshes[partType]) {
    state.partMeshes[partType] = [];
  }
  state.partMeshes[partType].push(model);
  mainModelGroup.add(model);

  state.loadedCount++;
  updateLoadingProgress();
}

function onModelProgress(xhr, name) {
  if (xhr.total) {
    const percent = Math.floor((xhr.loaded / xhr.total) * 100);
    const loadingEl = DOM.loadingIndicator;
    if (loadingEl && percent % 20 === 0) {
      loadingEl.textContent = `Loading ${name}... ${percent}%`;
    }
  }
}

function onModelError(error, name) {
  console.error(`Model "${name}" load failed:`, error);
  state.loadedCount++;
  updateLoadingProgress();
}

function updateLoadingProgress() {
  const total = state.totalModels;
  const loadingEl = DOM.loadingIndicator;

  if (loadingEl) {
    loadingEl.textContent = `Loading models... ${state.loadedCount}/${total}`;
  }

  if (state.loadedCount >= total) {
    state.isModelLoaded = true;
    setTimeout(() => {
      if (loadingEl) loadingEl.classList.add("hidden");
      applyAllColors();
      updateModelVisibility();
      console.log("所有模型加载完成！");
    }, 500);
  }
}

/* ============================================================
 *  颜色控制（按 partType）
 * ============================================================ */

function applyColorToPart(partType, colorHex) {
  if (!state.isModelLoaded) return;

  const color = new THREE.Color(colorHex);
  const meshes = state.partMeshes[partType] || [];

  meshes.forEach(model => {
    model.traverse(child => {
      if (child.isMesh && child.material) {
        child.material.color.copy(color);
        child.material.needsUpdate = true;
      }
    });
  });
}

function applyAllColors() {
  for (const partType in state.colorState) {
    if (state.colorState[partType]) {
      applyColorToPart(partType, state.colorState[partType]);
    }
  }
}

/* ============================================================
 *  模型可见性控制（深度/高度切换）
 * ============================================================ */

function updateModelVisibility() {
  // ---- 深度切换：crossmember ----
  const crossmemberParts = config.model.parts.crossmember || [];
  const crossmemberMeshes = state.partMeshes.crossmember || [];
  crossmemberMeshes.forEach((model, index) => {
    const modelInfo = crossmemberParts[index];
    if (modelInfo && modelInfo.depth) {
      model.visible = modelInfo.depth === state.activeDepth;
    }
  });

  // ---- 高度切换：height ----
  const heightParts = config.model.parts.height || [];
  const heightMeshes = state.partMeshes.height || [];
  heightMeshes.forEach((model, index) => {
    const modelInfo = heightParts[index];
    if (modelInfo && modelInfo.height) {
      model.visible = modelInfo.height === state.activeHeight;
    }
  });
}

/* ============================================================
 *  选项面板生成
 * ============================================================ */

function renderOptions() {
  const container = document.getElementById("builder-options");
  if (!container) return;
  container.innerHTML = "";

  config.options.forEach(option => {
    const node = document.createElement("div");
    node.className = "tree-node";
    node.dataset.optionId = option.id;

    const header = document.createElement("div");
    header.className = "tree-node-header";
    header.innerHTML = `
      <span class="node-title">${option.title}</span>
      <span class="toggle-icon">▼</span>
    `;
    header.addEventListener("click", () => {
      node.classList.toggle("collapsed");
    });

    const children = document.createElement("div");
    children.className = "tree-node-children";

    if (option.type === "color") {
      renderColorOption(option, children);
    } else if (option.type === "button") {
      renderButtonOption(option, children);
    } else if (option.type === "accessory") {
      renderAccessoryOption(option, children);
    }

    node.appendChild(header);
    node.appendChild(children);
    container.appendChild(node);
  });

  // 初始高亮选中状态
  updateActiveButtons();
}

/* ---- 颜色选项 ---- */
function renderColorOption(option, container) {
  const group = document.createElement("div");
  group.className = "option-group";

  const label = document.createElement("span");
  label.className = "color-label";
  label.textContent = option.title;
  group.appendChild(label);

  option.values.forEach(value => {
    const btn = document.createElement("button");
    btn.className = "color-btn";
    btn.style.background = value.color;
    btn.dataset.optionId = option.id;
    btn.dataset.value = value.value;
    btn.dataset.partType = option.partType || "";
    btn.dataset.color = value.color;
    btn.title = value.label + (value.price ? ` (+${config.currency}${value.price})` : "");
    btn.innerHTML = `<span class="color-check">✓</span>`;

    btn.addEventListener("click", () => {
      state.selectedOptions[option.id] = value.value;

      // 更新对应 partType 的颜色
      if (option.partType && value.color) {
        state.colorState[option.partType] = value.color;
        applyColorToPart(option.partType, value.color);
      }

      updateActiveButtons();
      updatePrice();
    });

    group.appendChild(btn);
  });

  container.appendChild(group);
}

/* ---- 按钮选项 ---- */
function renderButtonOption(option, container) {
  const group = document.createElement("div");
  group.className = "option-group";

  option.values.forEach(value => {
    const btn = document.createElement("button");
    btn.className = "option-btn";
    btn.dataset.optionId = option.id;
    btn.dataset.value = value.value;
    btn.textContent = value.label;

    btn.addEventListener("click", () => {
      state.selectedOptions[option.id] = value.value;

      // 深度切换
      if (option.id === "rack_depth") {
        state.activeDepth = value.value;
        updateModelVisibility();
      }

      // 高度切换
      if (option.id === "rack_height") {
        state.activeHeight = value.value;
        updateModelVisibility();
      }

      updateActiveButtons();
      updatePrice();
    });

    group.appendChild(btn);
  });

  container.appendChild(group);
}

/* ---- 配件选项 ---- */
function renderAccessoryOption(option, container) {
  const wrapper = document.createElement("div");
  wrapper.className = "accessory-buttons";

  option.values.forEach(acc => {
    const item = document.createElement("div");
    item.className = "accessory-item";
    item.dataset.value = acc.value;
    if ((state.accessories[acc.value] || 0) > 0) {
      item.classList.add("active");
    }

    const info = document.createElement("div");
    info.className = "accessory-info";

    // 配件图标
    if (acc.icon) {
      const icon = document.createElement("img");
      icon.className = "accessory-icon-img";
      icon.src = acc.icon;
      icon.alt = acc.label;
      info.appendChild(icon);
    }

    const textDiv = document.createElement("div");
    textDiv.innerHTML = `
      <div class="accessory-name">${acc.label}</div>
      <div class="accessory-price">+${config.currency}${acc.price}</div>
    `;
    info.appendChild(textDiv);

    // 数量控制
    const qtyControl = document.createElement("div");
    qtyControl.className = "quantity-control";

    const minusBtn = document.createElement("button");
    minusBtn.className = "quantity-btn";
    minusBtn.textContent = "−";

    const qtyValue = document.createElement("span");
    qtyValue.className = "quantity-value";
    qtyValue.textContent = state.accessories[acc.value] || 0;

    const plusBtn = document.createElement("button");
    plusBtn.className = "quantity-btn";
    plusBtn.textContent = "+";

    const updateQty = (newQty) => {
      if (newQty < 0) return;
      const max = acc.maxQty || 99;
      if (newQty > max) return;

      state.accessories[acc.value] = newQty;
      qtyValue.textContent = newQty;
      item.classList.toggle("active", newQty > 0);
      updatePrice();
    };

    minusBtn.addEventListener("click", e => {
      e.stopPropagation();
      updateQty((state.accessories[acc.value] || 0) - 1);
    });

    plusBtn.addEventListener("click", e => {
      e.stopPropagation();
      updateQty((state.accessories[acc.value] || 0) + 1);
    });

    // 点击整个 item 也可以切换
    item.addEventListener("click", e => {
      if (e.target.classList.contains("quantity-btn") ||
          e.target.classList.contains("quantity-value")) return;
      const current = state.accessories[acc.value] || 0;
      updateQty(current === 0 ? 1 : 0);
    });

    qtyControl.appendChild(minusBtn);
    qtyControl.appendChild(qtyValue);
    qtyControl.appendChild(plusBtn);

    item.appendChild(info);
    item.appendChild(qtyControl);
    wrapper.appendChild(item);
  });

  container.appendChild(wrapper);
}

/* ============================================================
 *  高亮当前选中按钮
 * ============================================================ */

function updateActiveButtons() {
  document.querySelectorAll(".color-btn, .option-btn").forEach(btn => {
    const optionId = btn.dataset.optionId;
    const value = btn.dataset.value;
    if (state.selectedOptions[optionId] === value) {
      btn.classList.add("active");
    } else {
      btn.classList.remove("active");
    }
  });
}

/* ============================================================
 *  价格计算
 * ============================================================ */

function calculateTotal() {
  let total = config.basePrice || 0;

  config.options.forEach(option => {
    if (option.type === "accessory") {
      // 配件价格 = 单价 x 数量
      option.values.forEach(acc => {
        const qty = state.accessories[acc.value] || 0;
        total += acc.price * qty;
      });
    } else {
      // 其他选项价格
      const selectedValue = state.selectedOptions[option.id];
      if (selectedValue) {
        const valueConfig = option.values.find(v => v.value === selectedValue);
        if (valueConfig && valueConfig.price) {
          total += valueConfig.price;
        }
      }
    }
  });

  return Math.max(0, total);
}

function updatePrice() {
  const total = calculateTotal();
  const formatted = config.currency + total.toLocaleString();
  const el = DOM.totalPrice;
  if (el) el.textContent = formatted;
}

/* ============================================================
 *  购物车辅助函数
 * ============================================================ */

// 获取选中的配件列表
function getActiveAccessories() {
  const accOption = config.options.find(o => o.type === "accessory");
  if (!accOption) return [];

  const result = [];
  accOption.values.forEach(acc => {
    const qty = state.accessories[acc.value] || 0;
    if (qty > 0) {
      result.push({
        name: acc.label,
        price: acc.price,
        quantity: qty
      });
    }
  });
  return result;
}

// 获取按钮选项的标题和选中值
function getSelectedOptions() {
  const options = {};
  config.options.forEach(option => {
    if (option.type === "button") {
      const selectedValue = state.selectedOptions[option.id];
      if (selectedValue) {
        const valueConfig = option.values.find(v => v.value === selectedValue);
        if (valueConfig) {
          options[option.title] = valueConfig.label;
        }
      }
    }
  });
  return options;
}

// 获取额外选项价格（Smith, Logo Plate, Cable System）
function getExtraOptionsPrice() {
  let total = 0;
  config.options.forEach(option => {
    if (option.type !== "button") return;

    const selectedValue = state.selectedOptions[option.id];
    if (!selectedValue) return;

    const valueConfig = option.values.find(v => v.value === selectedValue);
    if (valueConfig && valueConfig.price) {
      total += valueConfig.price;
    }
  });
  return total;
}

/* ============================================================
 *  Shopify 购物车
 * ============================================================ */

async function addToCart() {
  if (state.isAddingToCart) {
    showMessage("Please wait, adding to cart...", "error");
    return;
  }

  const activeAccessories = getActiveAccessories();
  if (activeAccessories.length === 0) {
    showMessage("Please select at least one accessory.", "error");
    return;
  }

  // 生成配件组合的 variant key
  const partOrder = config.shopify.partOrder || [];
  const selectedParts = activeAccessories.map(a => a.name);
  const sortedParts = [...selectedParts].sort((a, b) =>
    partOrder.indexOf(a) - partOrder.indexOf(b)
  );
  let variantKey = sortedParts.join("+");
  if (sortedParts.length === partOrder.length) {
    variantKey = "All accessores";
  }

  // 查找主变体 ID
  const mainVariantId = config.shopify.variantMap[variantKey];
  if (!mainVariantId) {
    showMessage(`Configuration "${variantKey}" not found.`, "error");
    return;
  }

  // 计算价格
  const partsTotal = activeAccessories.reduce((sum, a) => sum + a.price * a.quantity, 0);
  const extraPrice = getExtraOptionsPrice();
  const total = partsTotal + extraPrice;

  // 收集颜色信息
  const colorLabels = {
    main: "Main Frame",
    crossmember: "Crossmember",
    upright: "Upright",
    logo: "Logo Plate",
    jhook: "J-Hook",
    safety: "Safety Bar",
    base: "Base Support",
    pullup: "Pull-Up Bar"
  };

  const colorNameMap = config.colorNameMap || {};
  const colorLines = [];
  for (const [partType, label] of Object.entries(colorLabels)) {
    const colorHex = state.colorState[partType];
    if (colorHex) {
      const colorName = colorNameMap[colorHex.toLowerCase()] || colorNameMap[colorHex] || colorHex;
      colorLines.push(`  ${label}: ${colorName}`);
    }
  }
  const colorString = colorLines.join("\n");

  // 收集选项信息
  const selectedOptions = getSelectedOptions();
  const optionsString = Object.entries(selectedOptions)
    .map(([k, v]) => `  ${k}: ${v}`)
    .join("\n");

  // 获取深度和高度
  const depth = selectedOptions["RACK DEPTH"] || "41\"";
  const height = selectedOptions["RACK HEIGHT"] || "93\"";

  // 配件明细
  const partsList = activeAccessories.map(a =>
    `  ${a.name} x${a.quantity} ($${a.price * a.quantity})`
  ).join("\n");

  // 构建购物车项目
  const items = [{
    quantity: 1,
    id: mainVariantId,
    properties: {
      "_Config": sortedParts.join(" + "),
      "_Depth": depth,
      "_Height": height,
      "_Colors": colorString,
      "_Accessories": partsList,
      "_Options": optionsString,
      "_Subtotal": `$${partsTotal}`
    }
  }];

  // 添加额外选项作为独立购物车项目
  // Smith Attachment
  const smithValue = state.selectedOptions["smith_attachment"];
  if (smithValue) {
    const smithLabel = smithValue === "yes" ? "YES" : "NO";
    const smithId = config.shopify.variantMap[`Smith Attachment ${smithLabel}`];
    if (smithId) {
      items.push({
        quantity: 1,
        id: smithId,
        properties: { "_Option": "SMITH ATTACHMENT", "_Choice": smithLabel }
      });
    }
  }

  // Logo Plate
  const logoValue = state.selectedOptions["logo_plate"];
  if (logoValue) {
    const logoLabel = logoValue === "stainless" ? "STAINLESS" :
                      logoValue === "compact" ? "COMPACT" : "STANDARD";
    const logoId = config.shopify.variantMap[`Logo Plate ${logoLabel}`];
    if (logoId) {
      items.push({
        quantity: 1,
        id: logoId,
        properties: { "_Option": "LOGO PLATE", "_Choice": logoLabel }
      });
    }
  }

  // Cable System
  const cableValue = state.selectedOptions["cable_system"];
  if (cableValue) {
    const cableLabel = cableValue === "yes" ? "YES" : "NO";
    const cableId = config.shopify.variantMap[`Cable System ${cableLabel}`];
    if (cableId) {
      items.push({
        quantity: 1,
        id: cableId,
        properties: { "_Option": "CABLE SYSTEM", "_Choice": cableLabel }
      });
    }
  }

  // 发送加购请求
  state.isAddingToCart = true;
  const btn = DOM.addToCartBtn;
  if (btn) {
    btn.disabled = true;
    btn.textContent = "Adding...";
  }

  try {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items })
    });

    const result = await response.json();

    if (response.ok) {
      // 显示成功提示
      const resultEl = DOM.cartResult;
      const detailEl = DOM.cartResultDetail;
      const totalEl = DOM.cartResultTotal;

      if (resultEl) resultEl.classList.add("show");
      if (detailEl) {
        detailEl.innerHTML = `
          <div>Added <strong>${items.length}</strong> item(s)</div>
          <div style="font-size:11px;color:#888;margin-top:4px;white-space:pre-line;text-align:left;max-height:150px;overflow-y:auto;background:#f8f9fa;padding:8px;border-radius:4px;">
${colorLines.join("\n")}
${optionsString}
${partsList}</div>
        `;
      }
      if (totalEl) totalEl.textContent = config.currency + total.toLocaleString();

      showMessage("Item added to cart successfully!", "success");
      console.log("添加购物车完成");
    } else {
      showMessage(result.description || "Add to cart failed.", "error");
    }
  } catch (error) {
    console.error("Add to cart error:", error);
    showMessage("Add to cart failed, please try again.", "error");
  } finally {
    state.isAddingToCart = false;
    if (btn) {
      btn.disabled = false;
      btn.textContent = config.addToCartText || "Add to cart";
    }
  }
}

async function buyItNow() {
  await addToCart();
  setTimeout(() => {
    window.location.href = "/checkout";
  }, 1000);
}

/* ============================================================
 *  视角和缩放
 * ============================================================ */

function setCameraView(viewName) {
  const view = config.cameraViews?.[viewName];
  if (!view || !camera || !controls) return;

  const startPos = camera.position.clone();
  const endPos = new THREE.Vector3(view.position.x, view.position.y, view.position.z);
  const startTarget = controls.target.clone();
  const endTarget = new THREE.Vector3(view.target.x, view.target.y, view.target.z);

  const duration = 400;
  const startTime = performance.now();

  function tween(now) {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / duration, 1);
    const ease = 1 - Math.pow(1 - t, 3); // easeOutCubic

    camera.position.lerpVectors(startPos, endPos, ease);
    controls.target.lerpVectors(startTarget, endTarget, ease);
    controls.update();

    if (t < 1) requestAnimationFrame(tween);
  }

  requestAnimationFrame(tween);
}

function zoom(factor) {
  if (!camera || !controls) return;

  const distance = camera.position.distanceTo(controls.target);
  if (factor < 1 && distance > controls.minDistance + 0.5) {
    camera.position.multiplyScalar(factor);
    controls.update();
  } else if (factor > 1 && distance < controls.maxDistance - 0.5) {
    camera.position.multiplyScalar(factor);
    controls.update();
  }
}

/* ============================================================
 *  消息提示
 * ============================================================ */

function showMessage(text, type) {
  const container = DOM.messageContainer;
  if (!container) return;

  const msg = document.createElement("div");
  msg.className = type === "success" ? "success-message" : "error-message";
  msg.textContent = (type === "success" ? "✅ " : "⚠️ ") + text;

  container.innerHTML = "";
  container.appendChild(msg);

  if (window._messageTimer) clearTimeout(window._messageTimer);
  window._messageTimer = setTimeout(() => {
    container.innerHTML = "";
  }, 4000);
}

/* ============================================================
 *  事件绑定
 * ============================================================ */

function bindEvents() {
  // 视角按钮
  document.querySelectorAll("[data-view]").forEach(btn => {
    // 如果是默认视角，高亮该按钮
    const viewName = btn.dataset.view;
    if (viewName === (config._defaultView || "front")) {
      document.querySelectorAll("[data-view]").forEach(b => b.classList.remove("active"));
      btn.classList.add("active");
    }
    btn.addEventListener("click", () => {
      document.querySelectorAll("[data-view]").forEach(b => b.classList.remove("active"));
      btn.classList.add("active");
      setCameraView(btn.dataset.view);
    });
  });

  // 缩放按钮
  const zoomIn = DOM.zoomIn;
  const zoomOut = DOM.zoomOut;
  if (zoomIn) zoomIn.addEventListener("click", () => zoom(0.9));
  if (zoomOut) zoomOut.addEventListener("click", () => zoom(1.1));

  // 加入购物车
  const addBtn = DOM.addToCartBtn;
  if (addBtn) addBtn.addEventListener("click", addToCart);

  // 立即购买
  const checkoutBtn = DOM.checkoutBtn;
  if (checkoutBtn) checkoutBtn.addEventListener("click", buyItNow);

  // 继续购物按钮
  const continueBtn = DOM.continueBtn;
  if (continueBtn) {
    continueBtn.addEventListener("click", () => {
      const resultEl = DOM.cartResult;
      if (resultEl) resultEl.classList.remove("show");
    });
  }
}

/* ============================================================
 *  启动
 * ============================================================ */

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBuilder);
} else {
  initBuilder();
}
